import { Alert, Box, CircularProgress, Divider, Link, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/service/dataprovider';
import palette from '../../shared/theme/palette';
import Panel from '../../components/Panel';
import StatusChip from '../../components/StatusChip';

function leaseLabel(lease) {
  const unit = lease.unit ? `Unit ${lease.unit}` : '';
  const property = lease.propertyName || '';
  return [property, unit, lease.status].filter(Boolean).join(' · ');
}

function PersonRow({ person, currentPersonId }) {
  const { t } = useTranslation();
  const isCurrent = person.id === currentPersonId;
  return (
    <Stack gap={0.75} sx={{ py: 1 }}>
      <Typography sx={{ color: palette.grey[80], fontWeight: 500 }}>
        {person.firstName} {person.lastName}
        {isCurrent ? ` (${t('lease.resident.associationsThisResident')})` : ''}
      </Typography>
      {(person.leases ?? []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('lease.resident.associationsNoLeases')}
        </Typography>
      ) : (
        (person.leases ?? []).map(lease => (
          <Typography key={lease.id} variant="body2" color="text.secondary">
            {leaseLabel(lease)}
            {lease.hasOpenCreditAccount ? ` · ${t('lease.resident.associationsOpenCredBuild')}` : ''}
          </Typography>
        ))
      )}
    </Stack>
  );
}

/**
 * Shows every TenantPort login and ResMan person sharing this email.
 * Used when invite hits "User already exists" or the person is unregistered.
 */
export default function EmailAssociationsPanel({ email, currentPersonId, onConnectUser, connecting }) {
  const { t } = useTranslation();
  const normalized = (email || '').trim().toLowerCase();
  const { data, isLoading, error } = useQuery({
    queryKey: ['email-associations', normalized],
    queryFn: () => api.get('/v1/admin/email-associations', { params: { email: normalized } }),
    enabled: Boolean(normalized),
  });

  const payload = data?.data;
  const users = payload?.users ?? [];
  const personsWithoutUser = payload?.personsWithoutUser ?? [];

  return (
    <Panel padding={2} sx={{ mt: 2 }}>
      <Typography sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: palette.grey[50], mb: 1.5 }}>{t('lease.resident.associationsTitle')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('lease.resident.associationsSubtitle', { email: normalized })}
      </Typography>

      {isLoading && (
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={24} />
        </Stack>
      )}
      {error && <Alert severity="error">{error?.response?.data?.message ?? error?.response?.data?.err ?? t('lease.resident.associationsLoadFailed')}</Alert>}

      {!isLoading && !error && (
        <Stack gap={1.5}>
          {users.length === 0 && personsWithoutUser.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('lease.resident.associationsEmpty')}
            </Typography>
          )}

          {users.map(user => (
            <Box key={user.id}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography sx={{ color: palette.grey[80], fontWeight: 600 }}>
                  {t('lease.resident.associationsLogin')} · {user.email}
                </Typography>
                <StatusChip tone={user.status === 'ACTIVE' ? 'success' : 'neutral'} label={user.status} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('lease.resident.associationsPaymentSummary', {
                  count: user.activePaymentMethodCount ?? 0,
                  autopay: user.autopayActive ? t('lease.resident.associationsAutopayOn') : t('lease.resident.associationsAutopayOff'),
                })}
              </Typography>
              {(user.openCreditAccountLeaseIds ?? []).length > 0 && (
                <Typography variant="body2" color="warning.main">
                  {t('lease.resident.associationsCredBuildWarning')}
                </Typography>
              )}
              {user.person ? (
                <PersonRow person={user.person} currentPersonId={currentPersonId} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('lease.resident.associationsNoPerson')}
                </Typography>
              )}
              {user.person?.id !== currentPersonId && (
                <Link component="button" type="button" disabled={connecting} onClick={() => onConnectUser?.(user)} sx={{ mt: 0.5 }}>
                  {t('lease.resident.connectLoginToThisResident')}
                </Link>
              )}
              <Divider sx={{ mt: 1.5 }} />
            </Box>
          ))}

          {personsWithoutUser.length > 0 && (
            <>
              <Typography sx={{ fontWeight: 600, color: palette.grey[50], mt: 1 }}>{t('lease.resident.associationsOtherPersons')}</Typography>
              {personsWithoutUser.map(person => (
                <PersonRow key={person.id} person={person} currentPersonId={currentPersonId} />
              ))}
            </>
          )}
        </Stack>
      )}
    </Panel>
  );
}
